import { addBreadcrumb } from "./sentry";

type EventCategory =
  | "navigation"
  | "bible"
  | "chat"
  | "journal"
  | "growth"
  | "auth"
  | "share"
  | "engagement";

interface AnalyticsEvent {
  action: string;
  category: EventCategory;
  label?: string;
  value?: number;
}

export function trackEvent({ action, category, label, value }: AnalyticsEvent) {
  const data: Record<string, unknown> = { action, category };
  if (label) data.label = label;
  if (value !== undefined) data.value = value;

  addBreadcrumb(`${category}:${action}`, "analytics", data);

  if (import.meta.env.DEV) {
    console.debug("[Analytics]", category, action, label ?? "", value ?? "");
  }
}

export function trackScreenView(screenName: string) {
  trackEvent({ action: "view", category: "navigation", label: screenName });
}

export function trackEngagement(feature: string): () => void {
  const start = Date.now();
  return () => {
    const durationMs = Date.now() - start;
    trackEvent({
      action: "engagement",
      category: "engagement",
      label: feature,
      value: Math.round(durationMs / 1000),
    });
  };
}

export const analytics = {
  bibleChapterRead: (book: string, chapter: number) =>
    trackEvent({ action: "chapter_read", category: "bible", label: `${book} ${chapter}` }),
  bibleVerseHighlighted: (reference: string) =>
    trackEvent({ action: "verse_highlighted", category: "bible", label: reference }),
  bibleSearch: (query: string) =>
    trackEvent({ action: "search", category: "bible", label: query }),
  chatMessageSent: () =>
    trackEvent({ action: "message_sent", category: "chat" }),
  chatSessionStarted: () =>
    trackEvent({ action: "session_started", category: "chat" }),
  journalEntryCreated: (type: string) =>
    trackEvent({ action: "entry_created", category: "journal", label: type }),
  assessmentCompleted: () =>
    trackEvent({ action: "assessment_completed", category: "growth" }),
  dailyCheckInCompleted: () =>
    trackEvent({ action: "check_in_completed", category: "growth" }),
  signIn: (method: string) =>
    trackEvent({ action: "sign_in", category: "auth", label: method }),
  signUp: (method: string) =>
    trackEvent({ action: "sign_up", category: "auth", label: method }),
  signOut: () =>
    trackEvent({ action: "sign_out", category: "auth" }),
  collectionShared: (slug: string) =>
    trackEvent({ action: "collection_shared", category: "share", label: slug }),
  appOpened: (source: string) =>
    trackEvent({ action: "app_opened", category: "engagement", label: source }),
  deepLinkOpened: (path: string) =>
    trackEvent({ action: "deep_link_opened", category: "navigation", label: path }),
};
